import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, throwError } from 'rxjs';
import { SharedTestsLinkErrorType, SharedTestsResultsResponse } from '../../../shared/models/share-tests.model';
import { NotificationService } from '../../../shared/services/notification.service';
import { ExportService } from './../../../shared/services/export.service';

@Component({
  standalone: false,
  selector: 'app-shared-tests-index',
  templateUrl: 'shared-tests-index.component.html',
  styleUrls: ['./shared-tests-index.component.scss'],
})
export class SharedTestsIndexComponent {
  readonly exportService = inject(ExportService);
  readonly notificationService = inject(NotificationService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly titleService = inject(Title);

  // signals
  readonly token = signal<string>('');
  readonly loading = signal<boolean>(true);
  readonly errorType = signal<SharedTestsLinkErrorType>(null);
  readonly sharedTestsResultsInfo = signal<SharedTestsResultsResponse | null>(null);

  readonly errorTitle = computed(() => {
    switch (this.errorType()) {
      case 'invalid':
        return 'Invalid link';
      case 'expired':
        return 'Link expired';
      case 'notFound':
        return 'Link not found';
      default:
        return "Couldn't load shared tests";
    }
  });

  readonly errorMessage = computed(() => {
    switch (this.errorType()) {
      case 'invalid':
        return 'The shared tests URL is invalid.';
      case 'expired':
        return 'This shared tests link has expired. Please ask the sender to generate a new one.';
      case 'notFound':
        return 'This shared tests link was not found. It may have been removed or the URL may be incorrect.';
      default:
        return 'An unexpected error occurred while fetching shared tests. Please try again.';
    }
  });

  constructor() {
    this.titleService.setTitle('Shared Tests');

    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const token = params.get('token');

      if (token) {
        this.token.set(token);
        this.fetchSharedTests();
      } else {
        this.notificationService.error({
          title: 'Shared tests',
          message: 'Shared tests url is invalid',
        });
        this.errorType.set('invalid');
      }
    });
  }

  fetchSharedTests() {
    if (!this.token()) {
      return;
    }

    this.loading.set(true);
    this.errorType.set(null);

    this.exportService
      .getSharedLinkTestsResults(this.token())
      .pipe(
        catchError(e => {
          const isExpired = e.status === 410;
          const isNotFound = e.status === 404;

          const title = isExpired ? 'Shared tests link expired' : 'Shared tests error';

          const message = isExpired
            ? 'This shared tests link has expired'
            : isNotFound
              ? 'This shared tests link was not found'
              : e?.message || 'An error occurred while fetching shared tests';

          this.errorType.set(isExpired ? 'expired' : isNotFound ? 'notFound' : 'unknown');

          this.notificationService.error({
            title,
            message,
            requestMessage: e.statusText,
            requestCode: e.status,
          });

          return throwError(() => e);
        }),
        finalize(() => this.loading.set(false))
      )
      .subscribe(res => this.sharedTestsResultsInfo.set(res));
  }
}
